import { useState, useRef, useEffect, useCallback } from 'react';
import RelatoriosService from '../../services/RelatoriosService';
import { IAnalisarConfigurarResultado, IAssistenteComando, IAssistenteMensagemHistorico } from '../../interfaces/IAssistenteIa';
import { subscribeToEsteira } from '../../services/CardNotificationService';
import AssistenteIaRelatorioModal from './AssistenteIaRelatorioModal';

/*Tradução */
import '../../../i18n/config';
import { useTranslation } from 'react-i18next';

interface IMensagemChat {
    role: 'user' | 'assistant';
    texto: string;
    // DEVN3-609: mensagem que carrega um comando (card com custo/botões/andamento).
    comandoId?: number;
}

const MENSAGEM_INICIAL: IMensagemChat = {
    role: 'assistant',
    texto: 'Olá! Nesta esteira eu consigo:\n' +
        '1. Conciliar um cartão por IA (ex.: "concilia a conta 20004");\n' +
        '2. Analisar e configurar regras de contas de clientes e fornecedores: analiso o histórico, proponho regras, simulo sem gravar e explico o que sobraria pendente — você revisa o plano e decide se aplica. Vale para uma conta, várias ou todas de um grupo (ex.: "analise e configure as contas 20004 e 14" ou "analise e configure todas as contas de fornecedores");\n' +
        '3. Explicar por que lançamentos não conciliam (ex.: "por que a NF 1234 da conta 20004 não concilia?").\n' +
        'Antes de gastar com a IA eu sempre mostro o custo (provável e máximo) para você confirmar.'
};

const INTERVALO_POLLING_MS = 5000;
const STATUS_ATIVOS = ['PREPARANDO', 'AGUARDANDO_CONFIRMACAO', 'PROCESSANDO', 'AGUARDANDO_APLICACAO'];
// Em segundo plano no servidor: o chat consulta até mudar.
const STATUS_EM_SEGUNDO_PLANO = ['PREPARANDO', 'PROCESSANDO'];

const ROTULO_STATUS: Record<string, string> = {
    PREPARANDO: 'Calculando o custo',
    AGUARDANDO_CONFIRMACAO: 'Aguardando sua confirmação',
    PROCESSANDO: 'Processando',
    AGUARDANDO_APLICACAO: 'Proposta pronta',
    APLICADO: 'Regras aplicadas',
    CONCLUIDO: 'Concluído',
    CANCELADO: 'Cancelado',
    EXPIRADO: 'Estimativa expirada',
    LIMITE_CUSTO: 'Parado no limite de custo',
    ERRO: 'Erro'
};

const usd = (valor: number) => `US$ ${valor < 0.01 ? valor.toFixed(4) : valor.toFixed(2)}`;

// Custo provável/máximo do comando único (gravado no resultado desde a preparação).
const lerEstimativa = (comando: IAssistenteComando) => {
    if (comando.tipo !== 'ANALISAR_CONFIGURAR' || !comando.resultadoJson) return null;
    try {
        return (JSON.parse(comando.resultadoJson) as IAnalisarConfigurarResultado).estimativa ?? null;
    } catch {
        return null;
    }
};

const textoCusto = (comando: IAssistenteComando) => {
    const estimativa = lerEstimativa(comando);
    if (comando.status === 'PREPARANDO') return 'Nada é gasto com a IA nesta etapa';
    if (estimativa) {
        return comando.status === 'AGUARDANDO_CONFIRMACAO'
            ? `Custo da IA: provável ${usd(estimativa.provavelUsd)} · máximo ${usd(estimativa.maximoUsd)} (teto ${usd(comando.tetoUsd)})`
            : `Custo da IA: ${usd(comando.custoRealUsd)} (provável ${usd(estimativa.provavelUsd)}, máximo ${usd(estimativa.maximoUsd)})`;
    }
    return comando.status === 'AGUARDANDO_CONFIRMACAO'
        ? `Custo estimado da IA: até ${usd(comando.custoEstimadoUsd)} (teto ${usd(comando.tetoUsd)})`
        : `Custo da IA: ${usd(comando.custoRealUsd)} de até ${usd(comando.custoEstimadoUsd)}`;
};

const CardComando = ({ comando, ocupado, onAcao, onAbrir }: {
    comando: IAssistenteComando;
    ocupado: boolean;
    onAcao: (comando: IAssistenteComando, acao: 'confirmar' | 'cancelar' | 'aplicar-regras') => void;
    onAbrir: (comando: IAssistenteComando) => void;
}) => {
    const { status } = comando;
    const emSegundoPlano = STATUS_EM_SEGUNDO_PLANO.includes(status);
    const varias = comando.tipo === 'ANALISAR_CONFIGURAR';
    // No comando único o custo por conta já pode ser visto antes de confirmar.
    const temRelatorio = !!comando.resultadoJson
        && (['CONCLUIDO', 'APLICADO', 'AGUARDANDO_APLICACAO', 'LIMITE_CUSTO'].includes(status) || (varias && status === 'AGUARDANDO_CONFIRMACAO'));

    return (
        <div style={{ marginTop: 6, padding: 8, borderRadius: 8, border: '1px solid #D8DAE5', backgroundColor: '#F9F9FB', fontSize: 12 }}>
            <div style={{ fontWeight: 600 }}>{comando.descricao}</div>
            <div style={{ color: '#7E8299' }}>
                {ROTULO_STATUS[status] ?? status}
                {emSegundoPlano && comando.progresso ? ` — ${comando.progresso}` : ''}
            </div>
            <div style={{ color: '#7E8299' }}>{textoCusto(comando)}</div>
            {comando.erro && <div style={{ color: '#F1416C' }}>{comando.erro}</div>}

            <div style={{ display: 'flex', gap: 6, marginTop: 6, flexWrap: 'wrap' }}>
                {status === 'AGUARDANDO_CONFIRMACAO' && (
                    <>
                        <button className="btn btn-sm btn-primary py-1" disabled={ocupado} onClick={() => onAcao(comando, 'confirmar')}>Confirmar</button>
                        <button className="btn btn-sm btn-light py-1" disabled={ocupado} onClick={() => onAcao(comando, 'cancelar')}>Cancelar</button>
                    </>
                )}
                {emSegundoPlano && (
                    <>
                        <span className="spinner-border spinner-border-sm" style={{ color: '#5E6397' }}></span>
                        <button className="btn btn-sm btn-light py-1" disabled={ocupado} onClick={() => onAcao(comando, 'cancelar')}>Cancelar</button>
                    </>
                )}
                {status === 'AGUARDANDO_APLICACAO' && (
                    <>
                        {/* Várias contas: a escolha do que aplicar é feita no plano. */}
                        {!varias && <button className="btn btn-sm btn-primary py-1" disabled={ocupado} onClick={() => onAcao(comando, 'aplicar-regras')}>Aplicar regras</button>}
                        <button className="btn btn-sm btn-light py-1" disabled={ocupado} onClick={() => onAcao(comando, 'cancelar')}>Descartar</button>
                    </>
                )}
                {temRelatorio && (
                    <button className="btn btn-sm btn-light-primary py-1" onClick={() => onAbrir(comando)}>
                        {status === 'AGUARDANDO_APLICACAO' ? (varias ? 'Revisar plano e aplicar' : 'Ver proposta') : status === 'AGUARDANDO_CONFIRMACAO' ? 'Ver custo por conta' : 'Ver relatório'}
                    </button>
                )}
            </div>
        </div>
    );
};

// Ícone flutuante + painel de chat do assistente de IA. Aparece em /andamento-contas assim
// que uma esteira está carregada (mesma condição usada para abrir a conexão em tempo real
// do board, ver index.tsx) e só para usuários @ainumera.com (DEVN3-609 — a API aplica a mesma
// regra). Ver AssistenteIaController (Ainumera.Api) e IChatOrchestrator/AssistenteComandoService (Core).
const AssistenteIa = ({ esteiraId }: { esteiraId: number }) => {
    const { t } = useTranslation();
    const [aberto, setAberto] = useState<boolean>(false);
    const [mensagens, setMensagens] = useState<IMensagemChat[]>([MENSAGEM_INICIAL]);
    const [entrada, setEntrada] = useState<string>('');
    const [enviando, setEnviando] = useState<boolean>(false);
    const [cartaoMonitorado, setCartaoMonitorado] = useState<string | null>(null);
    const [comandos, setComandos] = useState<Record<number, IAssistenteComando>>({});
    const [comandoOcupado, setComandoOcupado] = useState<number | null>(null);
    const [relatorio, setRelatorio] = useState<IAssistenteComando | null>(null);
    const [retomado, setRetomado] = useState<boolean>(false);
    const listaRef = useRef<HTMLDivElement>(null);
    const comandosRef = useRef<Record<number, IAssistenteComando>>({});
    comandosRef.current = comandos;

    useEffect(() => {
        listaRef.current?.scrollTo({ top: listaRef.current.scrollHeight, behavior: 'smooth' });
    }, [mensagens, aberto, comandos]);

    // Troca de esteira: a conversa e os comandos são sempre da esteira selecionada.
    useEffect(() => {
        setMensagens([MENSAGEM_INICIAL]);
        setComandos({});
        setRetomado(false);
        setCartaoMonitorado(null);
    }, [esteiraId]);

    // Reaproveita o mesmo canal de status de cartão que o board já assina (Firestore, via
    // CardNotificationService) — quando o cartão enviado para análise sai de "Em
    // processamento", anexa uma mensagem final no chat. Sem canal de push novo.
    useEffect(() => {
        if (!cartaoMonitorado || !esteiraId) return;

        const unsubscribe = subscribeToEsteira(esteiraId, (evento) => {
            if (evento.conta !== cartaoMonitorado || evento.status === 'Em processamento') return;

            setMensagens((old) => [...old, {
                role: 'assistant',
                texto: `Terminei de analisar o cartão ${evento.conta} (status atual: ${evento.status}). As sugestões aparecem no card para você aceitar ou recusar.`
            }]);
            setCartaoMonitorado(null);
        });

        return () => unsubscribe();
    }, [cartaoMonitorado, esteiraId]);

    // Ao abrir o chat pela 1ª vez nesta esteira: retoma comandos ainda em aberto (ex.: análise que
    // continuou rodando no servidor depois que a página foi recarregada).
    useEffect(() => {
        if (!aberto || retomado || !esteiraId) return;
        setRetomado(true);

        RelatoriosService.listarComandosAssistente(esteiraId).then((lista) => {
            const abertos = lista.filter(c => STATUS_ATIVOS.includes(c.status)).reverse();
            if (abertos.length === 0) return;

            setComandos((old) => ({ ...old, ...Object.fromEntries(abertos.map(c => [c.id, c])) }));
            setMensagens((old) => [
                ...old,
                ...abertos.map(c => ({ role: 'assistant' as const, texto: 'Comando em aberto nesta esteira:', comandoId: c.id }))
            ]);
        });
    }, [aberto, retomado, esteiraId]);

    const aoMudarStatus = useCallback((anterior: IAssistenteComando | undefined, atual: IAssistenteComando) => {
        if (!anterior || anterior.status === atual.status) return;
        if (STATUS_EM_SEGUNDO_PLANO.includes(atual.status)) return;

        const texto = atual.resumo || atual.erro || ROTULO_STATUS[atual.status] || atual.status;
        setMensagens((old) => [...old, { role: 'assistant', texto, comandoId: atual.id }]);
    }, []);

    // Polling dos comandos em processamento (o trabalho pesado roda no servidor, em fatias).
    const idsProcessando = Object.values(comandos).filter(c => STATUS_EM_SEGUNDO_PLANO.includes(c.status)).map(c => c.id).join(',');
    useEffect(() => {
        if (!idsProcessando || !esteiraId) return;

        const timer = setInterval(async () => {
            for (const id of idsProcessando.split(',').map(Number)) {
                const atual = await RelatoriosService.obterComandoAssistente(esteiraId, id);
                if (!atual) continue;
                aoMudarStatus(comandosRef.current[id], atual);
                setComandos((old) => ({ ...old, [id]: atual }));
            }
        }, INTERVALO_POLLING_MS);

        return () => clearInterval(timer);
    }, [idsProcessando, esteiraId, aoMudarStatus]);

    const executarAcao = useCallback(async (comando: IAssistenteComando, acao: 'confirmar' | 'cancelar' | 'aplicar-regras', cartaoIds?: number[]) => {
        setComandoOcupado(comando.id);
        const resultado = cartaoIds
            ? await RelatoriosService.acaoComandoAssistente(esteiraId, comando.id, acao, cartaoIds)
            : await RelatoriosService.acaoComandoAssistente(esteiraId, comando.id, acao);
        setComandoOcupado(null);

        if (!resultado) {
            setMensagens((old) => [...old, { role: 'assistant', texto: 'Não consegui falar com o assistente agora. Tente novamente em instantes.' }]);
            return;
        }

        const atualizado = resultado.comando;
        if (atualizado) {
            setComandos((old) => ({ ...old, [atualizado.id]: atualizado }));
            if (relatorio?.id === atualizado.id) setRelatorio(acao === 'aplicar-regras' || acao === 'cancelar' ? null : atualizado);
        }

        // Explicação roda síncrona na confirmação: o texto já vem pronto no card.
        const comCard = atualizado && !STATUS_ATIVOS.includes(atualizado.status) ? atualizado.id : undefined;
        if (resultado.mensagem) {
            setMensagens((old) => [...old, { role: 'assistant', texto: resultado.mensagem, comandoId: comCard }]);
        }
    }, [esteiraId, relatorio]);

    const enviarMensagem = useCallback(async () => {
        const texto = entrada.trim();
        if (!texto || enviando) return;

        const historico: IAssistenteMensagemHistorico[] = mensagens.map((m) => ({ role: m.role, texto: m.texto }));

        setMensagens((old) => [...old, { role: 'user', texto }]);
        setEntrada('');
        setEnviando(true);

        const resposta = await RelatoriosService.enviarMensagemAssistente(esteiraId, texto, historico);

        if (!resposta) {
            setMensagens((old) => [...old, { role: 'assistant', texto: 'Não consegui falar com o assistente agora. Tente novamente em instantes.' }]);
            setEnviando(false);
            return;
        }

        const comando = resposta.comando;
        if (comando) {
            setComandos((old) => ({ ...old, [comando.id]: comando }));
        }
        setMensagens((old) => [...old, { role: 'assistant', texto: resposta.resposta, comandoId: comando?.id }]);

        if (resposta.acao?.tipo === 'conciliar_cartao' && resposta.acao.codigo) {
            setCartaoMonitorado(resposta.acao.codigo);
        }

        setEnviando(false);
    }, [entrada, enviando, mensagens, esteiraId]);

    if (!esteiraId) return null;

    return (
        <>
            <button
                onClick={() => setAberto((old) => !old)}
                title={t('Assistente de IA') as string}
                style={{
                    position: 'fixed', bottom: 24, right: 24, zIndex: 1050,
                    width: 56, height: 56, borderRadius: '50%', border: 'none',
                    backgroundColor: '#5E6397', color: '#fff', boxShadow: '0 4px 12px rgba(0,0,0,0.25)',
                    display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 22, cursor: 'pointer'
                }}
            >
                <i className={aberto ? 'fas fa-times' : 'fas fa-robot'}></i>
            </button>

            {aberto && (
                <div
                    style={{
                        position: 'fixed', bottom: 92, right: 24, zIndex: 1050,
                        width: 420, maxWidth: 'calc(100vw - 48px)', maxHeight: 560, display: 'flex', flexDirection: 'column',
                        backgroundColor: '#fff', borderRadius: 12, boxShadow: '0 8px 24px rgba(0,0,0,0.25)',
                        overflow: 'hidden', border: '1px solid #E4E6EF'
                    }}
                >
                    <div style={{ backgroundColor: '#5E6397', color: '#fff', padding: '10px 14px' }}>
                        <strong>{t('Assistente de IA')}</strong>
                        <div style={{ fontSize: 12, opacity: 0.85 }}>{t('Conciliação e análise de contas desta esteira')}</div>
                    </div>

                    <div ref={listaRef} style={{ flex: 1, overflowY: 'auto', padding: 12, backgroundColor: '#F4F5F7' }}>
                        {mensagens.map((m, idx) => {
                            const comando = m.comandoId ? comandos[m.comandoId] : undefined;
                            return (
                                <div key={idx} style={{ display: 'flex', justifyContent: m.role === 'user' ? 'flex-end' : 'flex-start', marginBottom: 8 }}>
                                    <div style={{
                                        maxWidth: '90%', padding: '8px 12px', borderRadius: 10, fontSize: 13, whiteSpace: 'pre-wrap',
                                        backgroundColor: m.role === 'user' ? '#5E6397' : '#fff',
                                        color: m.role === 'user' ? '#fff' : '#3F4254',
                                        border: m.role === 'user' ? 'none' : '1px solid #E4E6EF'
                                    }}>
                                        {m.texto}
                                        {comando && (
                                            <CardComando
                                                comando={comando}
                                                ocupado={comandoOcupado === comando.id}
                                                onAcao={executarAcao}
                                                onAbrir={setRelatorio}
                                            />
                                        )}
                                    </div>
                                </div>
                            );
                        })}
                        {enviando && (
                            <div style={{ fontSize: 12, color: '#7E8299', fontStyle: 'italic' }}>{t('Digitando...')}</div>
                        )}
                    </div>

                    <div style={{ display: 'flex', borderTop: '1px solid #E4E6EF', padding: 8, gap: 6 }}>
                        <input
                            type="text"
                            className="form-control form-control-sm"
                            placeholder={t('Ex.: analisa as contas de fornecedores') as string}
                            value={entrada}
                            disabled={enviando}
                            onChange={(e) => setEntrada(e.target.value)}
                            onKeyDown={(e) => { if (e.key === 'Enter') enviarMensagem(); }}
                        />
                        <button className="btn btn-sm btn-primary" disabled={enviando || !entrada.trim()} onClick={enviarMensagem}>
                            <i className="fas fa-paper-plane"></i>
                        </button>
                    </div>
                </div>
            )}

            <AssistenteIaRelatorioModal
                comando={relatorio ? comandos[relatorio.id] ?? relatorio : null}
                onFechar={() => setRelatorio(null)}
                onAplicar={(c, cartaoIds) => executarAcao(c, 'aplicar-regras', cartaoIds)}
                onDescartar={(c) => executarAcao(c, 'cancelar')}
            />
        </>
    );
};

export default AssistenteIa;
